import { NavLink } from "react-router-dom";
import { Brand } from "~/components/brand/Brand";
import { Icon } from "~/components/base/Icon";
import { useT } from "~/hooks/useT";
import { LANGS } from "~/lib/i18nConfig";
import { useCartStore } from "~/stores/cartStore";
import { useAuthStore } from "~/stores/authStore";

interface NavItemProps {
  to: string;
  icon: string;
  label: string;
  count?: number;
}

function NavItem({ to, icon, label, count }: NavItemProps) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `nav__item${isActive ? " nav__item--active" : ""}`
      }
    >
      <span className="ico">
        <Icon name={icon} size={15} />
      </span>
      <span>{label}</span>
      {typeof count === "number" && count > 0 && (
        <span className="count num">{count}</span>
      )}
    </NavLink>
  );
}

export function AppSidebar() {
  const { t, lang, setLang } = useT();
  const cartCount = useCartStore((s) => s.count());
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);

  const initial = user?.name?.[0] ?? "?";

  return (
    <aside className="nav desktop-only">
      <div className="nav__brand">
        <Brand height={12} withMark showChip />
      </div>

      <div className="nav__section">
        <div className="nav__section-title">{t("nav_main")}</div>
        <NavItem to="/dashboard" icon="dashboard" label={t("dashboard")} />
        <NavItem to="/catalog" icon="catalog" label={t("catalog")} />
        <NavItem to="/cart" icon="cart" label={t("cart")} count={cartCount} />
        <NavItem to="/orders" icon="orders" label={t("orders")} />
        <NavItem to="/invoices" icon="invoices" label={t("invoices")} />
      </div>

      <div className="nav__section">
        <div className="nav__section-title">{t("nav_account")}</div>
        <NavItem to="/account" icon="account" label={t("account")} />
      </div>

      <div className="nav__footer">
        {LANGS.length > 1 && (
          <div
            className="nav__lang"
            style={{
              display: "inline-flex",
              background: "var(--surface-3)",
              borderRadius: 6,
              padding: 2,
              marginBottom: 10,
              fontFamily: "var(--font-mono)",
              fontSize: 11,
            }}
          >
            {LANGS.map((l) => {
              const active = l === lang;
              return (
                <button
                  key={l}
                  type="button"
                  onClick={() => setLang(l)}
                  style={{
                    padding: "4px 8px",
                    borderRadius: 4,
                    background: active ? "var(--surface)" : "transparent",
                    color: active ? "var(--ink)" : "var(--ink-3)",
                    fontWeight: active ? 600 : 500,
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                    boxShadow: active ? "var(--shadow-xs)" : "none",
                    border: "none",
                    cursor: "pointer",
                  }}
                >
                  {l}
                </button>
              );
            })}
          </div>
        )}

        <div className="nav__user" title={t("logout")}>
          <div className="nav__avatar">{initial}</div>
          <div className="nav__user-meta grow truncate">
            <div className="nav__user-name truncate">
              {user?.name ?? t("guest")}
            </div>
            <div className="nav__user-role">{user?.role ?? ""}</div>
          </div>
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={logout}
            aria-label={t("logout")}
          >
            <Icon name="logout" size={14} />
          </button>
        </div>
      </div>
    </aside>
  );
}
